/**
 * CrewPilot page-layout categories and structural helpers.
 *
 * Structure only — does not restyle CP design-system components.
 * CSS: `src/layout/pageLayout.css` (+ `desktopPage.css` for the command canvas).
 *
 * Categories:
 * - command — Data / Command screens (full desktop canvas)
 * - hub     — Hub / Detail screens (desktop canvas + multi-column grids)
 * - form    — Focused Task / Form screens (readable ~720px column at ≥1024)
 * - phone   — legacy narrow `.screen` column (default ModuleShell)
 */

import { DESKTOP_PAGE_CLASS, withDesktopPageClass } from "./desktopPage";

export const PAGE_LAYOUT = Object.freeze({
  COMMAND: "command",
  HUB: "hub",
  FORM: "form",
  PHONE: "phone",
});

/** Structural class names (see pageLayout.css). */
export const PAGE_LAYOUT_CLASS = Object.freeze({
  COMMAND: DESKTOP_PAGE_CLASS,
  HUB: DESKTOP_PAGE_CLASS,
  FORM: "cp-page-form",
  STACK: "cp-page-stack",
  SECTION: "cp-page-section",
  GRID: "cp-page-grid",
  GRID_2: "cp-page-grid--2",
  GRID_3: "cp-page-grid--3",
  SPLIT: "cp-page-split",
  SPLIT_MAIN: "cp-page-split__main",
  SPLIT_ASIDE: "cp-page-split__aside",
  METRICS: "cp-page-metrics",
  DATA_SURFACE: "cp-page-data-surface",
  READABLE: "cp-page-readable",
});

/**
 * Whether this layout category opts into the shared desktop canvas.
 * @param {string} [layout]
 */
export function usesDesktopCanvas(layout) {
  return layout === PAGE_LAYOUT.COMMAND || layout === PAGE_LAYOUT.HUB;
}

/**
 * Compose ModuleShell / screen className for a page-layout category.
 * @param {string} [className]
 * @param {string} [layout]
 */
export function withPageLayoutClass(className = "", layout = PAGE_LAYOUT.PHONE) {
  if (usesDesktopCanvas(layout)) {
    return withDesktopPageClass(className, true);
  }
  if (layout === PAGE_LAYOUT.FORM) {
    return [PAGE_LAYOUT_CLASS.FORM, className].filter(Boolean).join(" ");
  }
  return className || "";
}

/**
 * Compose structural class names for grids / stacks.
 * @param  {...(string|false|null|undefined)} parts
 */
export function pageLayoutClass(...parts) {
  return parts.filter(Boolean).join(" ");
}
